// Auto-init for `cloudgrid plug` in an unlinked directory (Option E — see
// docs/brainstorms/2026-06-04-cli-usecase-trichotomy.md §4 Option E,
// spec v17 §7.1/§7.4).
//
// The Case-2 ("I already have an app, just deploy it") fast path. When `plug`
// runs in a directory with no `.cloudgrid/link.json` AND no `cloudgrid.yaml`,
// this module:
//   1. Sniffs the framework (framework-detect.ts).
//   2. Confirms with the user (TTY) — or proceeds silently with `--auto`.
//   3. Registers a server-side entity (POST /api/v2/entities), the same call
//      `cloudgrid init` makes.
//   4. Writes `.cloudgrid/link.json` and a generated `cloudgrid.yaml` whose one
//      `web` service is sourced from the directory root (`source.path: .`), so
//      the existing app's files are what get packaged — no scaffold, no
//      starter files written over the user's code.
//   5. Returns the LinkArtifact so the caller proceeds straight into the normal
//      linked-dir plug (build + deploy).
//
// It never builds or deploys — that's the caller's job once a link is in hand.
// Decision 012: yaml is shape-only; entity identity lives in link.json + on the
// server. Decision 124: `source.path` lets a service pack from a non-default
// directory (here, the project root).

import { existsSync } from 'node:fs';
import { basename } from 'node:path';

import inquirer from 'inquirer';

import { CloudGridError } from '../utils.js';
import type { IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { getActiveOrg, notSignedInMessage } from '../active-org.js';
import {
  appendGitignoreLine,
  writeLink,
  type LinkArtifact,
} from './link.js';
import { writeGeneratedYaml } from './generated-yaml.js';
import {
  detectFramework as defaultDetectFramework,
  type FrameworkDetection,
} from './framework-detect.js';
import { emitFrameworkDetectUnknown } from './framework-detect-telemetry.js';
import { suggestSlug } from '../commands/slug.js';

export interface AutoInitOpts {
  /** `--org <slug>` — one-shot active-org override. */
  org?: string;
  /** `--auto` — skip the confirm prompt (CI / non-interactive intent). */
  auto?: boolean;
  /** `--name <slug>` — override the slug auto-derived from the directory name.
   *  Lets the user recover when the dir name yields no valid slug (e.g. an
   *  all-digit folder) without renaming the directory. */
  name?: string;
}

export interface AutoInitDeps {
  /** Injectable framework sniffer (tests pass a stub). */
  detect?: (dir: string) => FrameworkDetection | null;
  /** Injectable yes/no confirm. Defaults to an inquirer confirm. */
  confirm?: (message: string, defaultValue?: boolean) => Promise<boolean>;
  /** Injectable slug prompt for the ambiguous-basename case. */
  promptSlug?: (message: string, defaultValue: string) => Promise<string>;
  /** Override TTY detection (tests force a value). */
  isTTY?: boolean;
  /** Output sink. Defaults to stdout. */
  writer?: (s: string) => void;
  /** CLI version stamped into link.json. */
  cliVersion: string;
  /** Injectable telemetry emitter for the framework-detect fall-through
   *  (backlog 462). Fire-and-forget; defaults to the real best-effort emitter.
   *  Tests pass a spy to assert it fires on the `node` catch-all only. */
  emitUnknown?: (dir: string) => void;
}

/** Shape of the POST /api/v2/entities response we read. Mirrors init.ts. */
interface EntityResponse {
  entity_id: string;
  name: string;
  kind: 'app' | 'agent';
  org_id: string;
}

/**
 * Result of an auto-init attempt:
 *   - `linked`   — an entity was created and the dir is now linked; the caller
 *                  proceeds to plug with `link`.
 *   - `declined` — the user said no at the confirm prompt; the caller exits
 *                  cleanly with no error.
 */
export type AutoInitResult =
  | { kind: 'linked'; link: LinkArtifact }
  | { kind: 'declined' };

/**
 * Run the auto-init flow for an unlinked, yaml-less directory.
 *
 * Throws CloudGridError when:
 *   - the framework can't be detected (caller should suggest `cloudgrid init`),
 *   - the run is non-interactive and `--auto` was not passed (we never mutate a
 *     directory + create a server entity silently without explicit opt-in),
 *   - a slug can't be derived and there's no TTY to prompt for one,
 *   - entity creation fails server-side.
 */
export async function runAutoInit(
  baseClient: IV2Client,
  cwd: string,
  opts: AutoInitOpts,
  deps: AutoInitDeps,
): Promise<AutoInitResult> {
  const detect = deps.detect ?? defaultDetectFramework;
  const writer = deps.writer ?? ((s: string) => process.stdout.write(s));
  const isTTY =
    deps.isTTY ?? Boolean(process.stdout.isTTY && process.stdin.isTTY);

  const emitUnknown =
    deps.emitUnknown ?? ((dir: string) => void emitFrameworkDetectUnknown(dir));

  const detection = detect(cwd);
  if (!detection) {
    throw new CloudGridError(
      [
        "This directory isn't linked to a CloudGrid node, and I couldn't tell what kind of app it is.",
        'Run: cloudgrid init <app|agent> <slug>   to set it up explicitly.',
      ].join('\n'),
    );
  }

  // Backlog 462: the detector reached its `node` catch-all without matching any
  // specific framework. Auto-init only runs when there is no cloudgrid.yaml, so
  // the user never declared a `type:` — this is exactly the "unknown stack"
  // case we want signal on. Emit a privacy-safe telemetry event before the
  // confirm prompt so we capture the gap even if the user then backs out.
  // Fire-and-forget; it never blocks or fails the plug.
  if (detection.type === 'node') {
    emitUnknown(cwd);
  }

  // Non-interactive runs must opt in with --auto. Creating a server entity and
  // writing files is a real side effect; we never do it silently in CI.
  if (!opts.auto && !isTTY) {
    throw new CloudGridError(
      [
        `This directory isn't linked. I see a ${detection.label}.`,
        'Re-run with --auto to set it up and plug without a prompt, or run cloudgrid init.',
      ].join('\n'),
    );
  }

  // An explicit `--name <slug>` wins over the directory-derived slug. This is
  // the escape hatch surfaced in the error below: a dir like `2024` (all
  // digits) yields no valid slug, and renaming a real project's folder is
  // surprising — `--name` lets the user pick an identifier instead.
  let slug: string | null;
  if (opts.name !== undefined) {
    slug = suggestSlug(opts.name);
    if (slug === null) {
      throw new CloudGridError(
        `Invalid app name: ${opts.name} (must be 2-42 chars, lowercase ` +
          `alphanumeric + hyphens, starting with a letter, no trailing hyphen).`,
      );
    }
  } else {
    // Derive a slug from the directory name; prompt (TTY) when it doesn't yield
    // a valid one.
    slug = suggestSlug(basename(cwd));
  }
  if (slug === null) {
    if (!isTTY) {
      throw new CloudGridError(
        [
          `Invalid app name: ${basename(cwd)} (couldn't derive a valid slug ` +
            `from the directory name).`,
          'Pass --name <slug> to use a different identifier, or run',
          'cloudgrid init <app|agent> <slug> to set it up explicitly.',
        ].join('\n'),
      );
    }
    const promptSlug =
      deps.promptSlug ??
      (async (message: string, defaultValue: string) => {
        const ans = await inquirer.prompt([
          { type: 'input', name: 'slug', message, default: defaultValue },
        ]);
        return String(ans.slug ?? '');
      });
    const answer = (await promptSlug('Pick a slug for the outlet URL:', 'my-app')).trim();
    slug = suggestSlug(answer);
    if (slug === null) {
      throw new CloudGridError(
        `'${answer}' is not a valid slug (lowercase alphanumeric + hyphens, 3-40 chars).`,
      );
    }
  }

  // Confirm unless --auto. §23 voice: plain, no exclamation.
  if (!opts.auto) {
    const confirm =
      deps.confirm ??
      (async (message: string, defaultValue = true) => {
        const ans = await inquirer.prompt([
          { type: 'confirm', name: 'ok', message, default: defaultValue },
        ]);
        return Boolean(ans.ok);
      });
    const ok = await confirm(
      `No CloudGrid config here. I see a ${detection.label}. Set up '${slug}' and plug it?`,
      true,
    );
    if (!ok) {
      writer(
        'Stopped. Nothing was created. Run cloudgrid init to set it up manually, ' +
          'or cloudgrid plug --auto to skip this prompt.\n',
      );
      return { kind: 'declined' };
    }
  }

  // Resolve the active org server-side, then bind the client to it so the
  // entity is created in the right hub. Mirrors init.ts / plug --existing.
  let active: { org_id: string; slug: string };
  try {
    active = await getActiveOrg(baseClient, { override: opts.org });
  } catch (err) {
    if (err instanceof V2ApiError) throw mapEntityError(err);
    throw err;
  }
  const client = baseClient.withOrgSlug(active.slug);

  // Re-check the yaml right before any server mutation. The outer guard in
  // plug.ts ran before the confirm prompt + org resolve; if a yaml appeared
  // since (git pull during the prompt, a teammate's concurrent write), bail
  // BEFORE creating a server entity. Otherwise we'd leave a dangling entity
  // whose dir has a yaml we never wrote.
  if (existsSync(`${cwd}/cloudgrid.yaml`)) {
    writer(
      'A cloudgrid.yaml appeared while you were deciding. Stopped — nothing was created.\n' +
        'Re-run cloudgrid plug to use it.\n',
    );
    return { kind: 'declined' };
  }

  let entity: EntityResponse;
  try {
    entity = await client.post<EntityResponse>('/api/v2/entities', {
      kind: 'app',
      name: slug,
    });
  } catch (err) {
    if (err instanceof V2ApiError) throw mapEntityError(err);
    throw err;
  }

  // The server may have suffixed the slug on collision; surface it once.
  if (entity.name !== slug) {
    writer(
      `Note: '${slug}' was taken in ${active.slug}-hub; using '${entity.name}'.\n`,
    );
  }

  const link: LinkArtifact = {
    entity_id: entity.entity_id,
    org_id: entity.org_id ?? active.org_id,
    org_slug: active.slug,
    kind: entity.kind === 'agent' ? 'agent' : 'app',
    entity_slug: entity.name,
    created_at: new Date().toISOString(),
    cli_version: deps.cliVersion,
  };
  writeLink(link, cwd);
  appendGitignoreLine('.cloudgrid/', cwd);
  appendGitignoreLine('.env.local', cwd);

  // Generate a cloudgrid.yaml describing the detected service, sourced from the
  // directory root. Guard against an unexpected pre-existing yaml — the caller
  // only routes us here when none exists, but never overwrite if one appeared.
  if (!existsSync(`${cwd}/cloudgrid.yaml`)) {
    writeGeneratedYaml(cwd, {
      name: entity.name,
      type: detection.type,
      ...(detection.build ? { build: detection.build } : {}),
    });
  }

  writer(
    `Initialising ${entity.name} (app) in ${active.slug}-hub...\n` +
      `  Created cloudgrid.yaml (${detection.type}, sourced from .)\n` +
      `  Linked .cloudgrid/link.json\n` +
      '  Charged. Plugging now...\n',
  );

  return { kind: 'linked', link };
}

/** Map POST /api/v2/entities (and active-org) errors to §6.5-style messages. */
function mapEntityError(err: V2ApiError): CloudGridError {
  if (
    err.code === 'AUTH_REQUIRED' ||
    err.code === 'AUTH_EXPIRED' ||
    err.code === 'AUTH_INVALID'
  ) {
    return new CloudGridError(notSignedInMessage());
  }
  if (err.code === 'FORBIDDEN_ROLE') {
    return new CloudGridError(
      'Creating an entity requires admin or developer role on this grid.',
    );
  }
  if (err.code === 'CONFLICT') {
    return new CloudGridError(err.message);
  }
  if (err.code === 'VALIDATION_FAILED') {
    return new CloudGridError(err.message);
  }
  return new CloudGridError(
    `Server error (${err.status} ${err.code}). Try again, or run cloudgrid feedback if it persists.`,
  );
}
